import { Bullet, Circle, Label, Tooltip, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  DateAxis,
  ISmoothedXLineSeriesSettings,
  SmoothedXLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import type { AxisLabel } from "@amcharts/amcharts5/xy";
import { format } from "date-fns";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { createDimensionNames, createStackedDimensionNames, parseDateByMetric } from "@/helpers/dataMapping";
import { VISUAL_QUARTER_FORMAT, stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { CAPITAL_OTHER_VALUE, LOWERCASE_OTHER_VALUE } from "@/settings/constants";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class StackedArea100Chart extends BaseChart {
  isStackedArea100Chart!: boolean;
  timeMetric = [] as ChartDataRecord[];
  metrics = [] as string[];

  initChart() {
    this.isStackedArea100Chart = this.chartName === "stackedArea100Chart";
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        maxTooltipDistance: -1,
      })
    );

    this.createAxes();
    this.createCursor();

    this.metrics.forEach((name: string, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      this.createSeries(name, indexColor, name);
    });

    this.showLegend([...this.metrics], (this.chart as XYChart).series.values as LegendData);
  }

  protected createSeries(name: string, index: number, fieldName: string) {
    const isQuarter = this.settings.decompositionMetric === "quarter";
    const seriesConfig: ISmoothedXLineSeriesSettings = {
      name,
      stacked: true,
      xAxis: this.axes.xAxis,
      yAxis: this.axes.yAxis,
      valueYField: name,
      fill: this.getSeriesColor(index),
    };

    if (isQuarter) {
      seriesConfig.categoryXField = this.settings.decompositionMetric;
    } else {
      seriesConfig.valueXField = "timestamp";
    }

    const series = SmoothedXLineSeries.new(this.root, seriesConfig);
    (this.chart as XYChart).series.push(series);

    series.bullets.push(() => {
      const graphics = Circle.new(this.root, {
        radius: 4,
        fill: series.get("fill"),
        tooltip: Tooltip.new(this.root, {}),
        tooltipText: undefined,
        showTooltipOn: "hover",
      });
      return Bullet.new(this.root, { sprite: graphics });
    });

    series.fills.template.setAll({
      visible: true,
      fillOpacity: 0.2,
    });

    this.createLegendLabel(series, fieldName);
    series.setAll({ locationX: 0.5 });

    series.data.setAll(this.data);

    if (this.isStackedArea100Chart) {
      series.set("valueYShow", "valueYTotalPercent");
    }

    this.createTooltip(series, () => {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const dataItem = series.get("tooltip")?.dataItem as any;
      if (!dataItem) return [];

      const values = dataItem.dataContext as Record<string, string | number>;
      const rawDate = isQuarter ? values[this.settings.decompositionMetric] : dataItem.get("valueX");

      const date = isQuarter ? stringOrNumberToDate(rawDate) : new Date(rawDate);
      if (isNaN(date.getTime())) return [];

      const value = values[name];

      return [
        { key: this.settings.decompositionMetricTop, value: name },
        { key: this.settings.decompositionMetric, value: format(date, this.getTooltipDateFormat()) },
        { key: this.settings.displayMetric, value },
      ];
    });
  }

  protected createAxes() {
    let xAxis: any;

    if (this.settings.decompositionMetric === "quarter") {
      const xRenderer = AxisRendererX.new(this.root, {
        minGridDistance: 30,
      });
      xRenderer.labels.template.setAll({ ...chartFontSettings.label });

      xAxis = (this.chart as XYChart).xAxes.push(
        CategoryAxis.new(this.root, {
          categoryField: this.settings.decompositionMetric,
          renderer: xRenderer,
          startLocation: 0.5,
          endLocation: 0.5,
        })
      );

      xAxis.data.setAll(this.data);

      xAxis.get("renderer").labels.template.adapters.add("text", (text: string | undefined, target: AxisLabel) => {
        const data = target.dataItem?.dataContext as Record<string, any> | undefined;
        const raw = data?.[this.settings.decompositionMetric];
        const date = stringOrNumberToDate(raw);
        return isNaN(date.getTime()) ? text : format(date, VISUAL_QUARTER_FORMAT);
      });

      if (this.settings.xAxisTitle) {
        xAxis.children.push(
          Label.new(this.root, {
            text: this.settings.xAxisTitle,
            ...chartFontSettings.axisTitle,
            x: p50,
            marginTop: axisTitle.marginTop,
            centerX: p50,
          })
        );
      }
    } else {
      const xRenderer = AxisRendererX.new(this.root, {
        minGridDistance: 30,
      });
      xRenderer.labels.template.setAll({ ...chartFontSettings.label });

      const baseInterval = this.getBaseInterval();

      xAxis = (this.chart as XYChart).xAxes.push(
        DateAxis.new(this.root, {
          baseInterval,
          renderer: xRenderer,
          dateFormats: getAxisDateFormat(this.settings.decompositionMetric),
          periodChangeDateFormats: getAxisDateFormat(this.settings.decompositionMetric),
          strictMinMax: true,
          markUnitChange: true,
        })
      );

      this.configureXAxisForTimeUnit(xAxis, this.settings.decompositionMetric);

      const dateValues = this.data.map((d: any) =>
        d.timestamp instanceof Date
          ? d.timestamp.getTime()
          : parseDateByMetric(d.timestamp, this.settings.decompositionMetric).getTime()
      );

      const sorted = [...dateValues].sort((a, b) => a - b);
      const first = new Date(sorted[0]);
      const last = new Date(sorted[sorted.length - 1]);

      let minDate: Date, maxDate: Date;
      if (this.settings.decompositionMetric === "month") {
        minDate = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
        maxDate = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 1, 1));
      } else {
        minDate = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate()));
        maxDate = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate() + 1));
      }

      xAxis.set("min", minDate.getTime());
      xAxis.set("max", maxDate.getTime());

      xAxis.setAll({
        startLocation: 0,
        endLocation: 1,
      });

      this.root.events.once("frameended", () => {
        xAxis.zoomToDates(minDate, maxDate);
      });

      xAxis.get("renderer").labels.template.set("location", 0.5);
      xAxis.get("renderer").grid.template.set("location", 0.5);

      if (this.settings.xAxisTitle) {
        xAxis.children.push(
          Label.new(this.root, {
            text: this.settings.xAxisTitle,
            ...chartFontSettings.axisTitle,
            x: p50,
            marginTop: axisTitle.marginTop,
            centerX: p50,
          })
        );
      }
    }

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({ ...chartFontSettings.label });

    const maxValue = Math.max(
      ...this.data.map((row) => this.metrics.reduce((acc, key) => acc + (Number(row[key]) || 0), 0))
    );
    const paddedMax = Math.ceil(maxValue / 10) * 10 * 1.1;

    const yAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRenderer,
        min: this.isStackedArea100Chart ? 0 : 0,
        max: this.isStackedArea100Chart ? 100 : paddedMax,
        maxDeviation: 0,
        strictMinMax: true,
        calculateTotals: this.isStackedArea100Chart,
        extraMax: 0.1,
        numberFormat: this.isStackedArea100Chart
          ? "#'%'"
          : this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
      })
    );

    if (this.settings.yAxisTitle) {
      yAxis.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
          marginTop: axisTitle.marginTop,
        })
      );
    }

    this.axes = { xAxis, yAxis };
  }

  private configureXAxisForTimeUnit(xAxis: DateAxis<any>, timeUnit: string) {
    const renderer = xAxis.get("renderer");

    if (timeUnit === "day") {
      renderer.set("minGridDistance", 80);
      renderer.labels.template.setAll({
        ...chartFontSettings.label,
        rotation: -45,
        centerX: 0,
        centerY: percent(50),
      });
    } else {
      renderer.set("minGridDistance", 30);
      renderer.labels.template.setAll({ ...chartFontSettings.label });
    }
  }

  private getBaseInterval(): { timeUnit: "day" | "month"; count: number } {
    switch (this.settings.decompositionMetric) {
      case "quarter":
        return { timeUnit: "month", count: 3 };
      case "month":
        return { timeUnit: "month", count: 1 };
      case "day":
        return { timeUnit: "day", count: 1 };
      default:
        return { timeUnit: "month", count: 1 };
    }
  }

  private getTooltipDateFormat(): string {
    switch (this.settings.decompositionMetric) {
      case "quarter":
        return VISUAL_QUARTER_FORMAT;
      case "month":
        return "MMM yyyy";
      case "day":
        return "MMM dd, yyyy";
      default:
        return "MMM yyyy";
    }
  }

  protected mapData(data: []): ChartDataRecord[] {
    const firstDimension = this.settings.decompositionMetric;
    const secondDimension = this.settings.decompositionMetricTop;
    const displayMetric = this.settings.displayMetric;
    const isQuarter = firstDimension === "quarter";

    this.metrics = [];
    this.timeMetric = [];

    createStackedDimensionNames(data, firstDimension, this.timeMetric);
    createDimensionNames(data, secondDimension, this.metrics);

    if (this.metrics.includes(LOWERCASE_OTHER_VALUE)) {
      this.metrics = this.metrics.filter((m) => m !== LOWERCASE_OTHER_VALUE);
    }
    if (!this.metrics.includes(CAPITAL_OTHER_VALUE)) {
      this.metrics.push(CAPITAL_OTHER_VALUE);
    }

    const dates = Array.from(new Set(data.map((d: any) => d[firstDimension])));

    const mappedData = dates
      .map((date) => {
        const group = data.filter((d: any) => d[firstDimension] === date);

        const record: Record<string, number | string | Date> = {};
        this.metrics.forEach((metric) => {
          record[metric] = 0;
        });

        group.forEach((item) => {
          const key = item[secondDimension];
          const value = Number(item[displayMetric] || 0);

          if (key === LOWERCASE_OTHER_VALUE) {
            record[CAPITAL_OTHER_VALUE] = ((record[CAPITAL_OTHER_VALUE] || 0) as number) + value;
          } else {
            record[key] = ((record[key] || 0) as number) + value;
          }
        });

        if (isQuarter) {
          record[firstDimension] = String(date);
        } else {
          const parsed = parseDateByMetric(date, firstDimension);
          record.timestamp = parsed.getTime();
        }

        return record as ChartDataRecord;
      })
      .filter((item) => {
        const sum = this.metrics.reduce((acc, key) => acc + (Number(item[key]) || 0), 0);
        return sum > 0;
      });

    mappedData.sort((a, b) => {
      if (isQuarter) {
        return String(a[firstDimension]).localeCompare(String(b[firstDimension]));
      }

      const aDate = a.timestamp instanceof Date ? a.timestamp.getTime() : Number(a.timestamp);
      const bDate = b.timestamp instanceof Date ? b.timestamp.getTime() : Number(b.timestamp);

      return aDate - bDate;
    });

    return mappedData;
  }

  protected createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.xAxis,
      })
    );
    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", true);
  }
}
